import { useEffect, useState } from "react";
import { Alert, Box, DialogActions, Typography } from "@mui/material";
import AppButton from "../components/AppButton";
import { useSnackbar } from "notistack";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQuery } from "react-query";

import { CustomFormProps } from "../Configuration/interfaces";
import { SX_FORM_CANCEL, SX_FORM_SUBMIT } from "../Configuration/styles";
import ApplyDialog from "./ApplyDialog";
import GridEditor from "./GridEditor";
import {
  createGrid,
  fetchGridProducts,
  fetchProposedGrid,
  updateGrid,
} from "./api";
import {
  draftFromProposedGrid,
  hasAnyPrice,
  toDraft,
  toPayload,
  validateDraft,
} from "./grid";
import {
  GridDraft,
  PricingTierGrid,
  PricingTierGridPayload,
  ProposedGrid,
  PropertyOption,
  RoundingDirection,
  UhTypeFollow,
} from "./types";

/**
 * The ladder editor, as the configuration table's create and edit dialog.
 *
 * Only the body and its footer: the list, the filters and the dialog shell come
 * from the declarative page, the way the quoting studio plugs into its own.
 */
const LadderForm = ({
  row,
  onClose,
  refetch,
  onWidthChange,
}: CustomFormProps) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  // The table hands back the row the list serializer produced, which is the
  // whole grid, tiers included.
  const grid = (row as PricingTierGrid | undefined) ?? null;

  const [name, setName] = useState(grid?.name ?? "");
  const [roundingDirection, setRoundingDirection] = useState<RoundingDirection>(
    grid?.rounding_direction ?? "nearest"
  );
  const [uhTypeFollow, setUhTypeFollow] = useState<UhTypeFollow>(
    grid?.uh_type_follow ?? "same_steps"
  );
  // The grid belongs to a hotel; picking one brings in every category it
  // prices, as rows, without a selection step.
  const [property, setProperty] = useState<PropertyOption | null>(
    grid?.property ?? null
  );
  const [draft, setDraft] = useState<GridDraft>({
    levels: [],
    products: [],
  });

  const onError = (error: unknown) => {
    enqueueSnackbar(
      error instanceof Error
        ? error.message
        : intl.formatMessage({ id: "ERROR_OCCURRED" }),
      { variant: "error" }
    );
  };

  // Dropped the moment the analyst clears the grid: the strip claims the
  // prices on screen came from the history, and it must stop saying so.
  const [proposal, setProposal] = useState<ProposedGrid | null>(null);

  // A grid being edited is read from what was saved. A new one opens on the
  // hotel's own last twelve months: ninety blank cells is what stops a ladder
  // from ever being created.
  const productsQuery = useQuery(
    ["pricing-tier-products", property?.id],
    () => fetchGridProducts(property!.id),
    {
      // Fetched on creation too, though the draft then comes from the
      // proposal: this is also where the editor reads the recommendation
      // threshold it draws the steps against.
      enabled: property != null,
      onSuccess: (columns) => {
        if (grid == null) return;
        setDraft(toDraft(grid.tiers, columns.products));
      },
    }
  );

  const proposalQuery = useQuery(
    ["pricing-tier-proposal", property?.id],
    () => fetchProposedGrid(property!.id),
    {
      enabled: property != null && grid == null,
      onSuccess: (result) => {
        setProposal(result);
        setDraft(draftFromProposedGrid(result));
      },
      onError,
    }
  );
  const loading = productsQuery.isLoading || proposalQuery.isLoading;

  const errorKey = validateDraft(draft);
  const canSave = property !== null && errorKey === null && name.trim() !== "";
  // Nothing is wrong yet on a form nobody has filled in: hold the message until
  // the analyst has typed something or asked to save.
  const [submitted, setSubmitted] = useState(false);
  const dirty = hasAnyPrice(draft);
  const showError = errorKey !== null && (submitted || dirty);

  const isShared = (grid?.assignment_count ?? 0) > 0;

  // Narrow until there is a grid to show. A dialog that opens on one
  // dropdown and fills the screen with empty space reads as a page that
  // failed to load; it widens once, when the hotel brings its rows.
  useEffect(() => {
    onWidthChange?.(property ? "xl" : "sm");
  }, [property, onWidthChange]);

  // The saved grid whose impact is being checked, straight after a save or from
  // the "check" button.
  const [checking, setChecking] = useState<PricingTierGrid | null>(null);

  const payload = (propertyId: number): PricingTierGridPayload => ({
    property: propertyId,
    name: name.trim(),
    rounding_direction: roundingDirection,
    uh_type_follow: uhTypeFollow,
    ...toPayload(draft),
  });

  const save = useMutation(
    () => {
      if (property === null) {
        return Promise.reject(new Error("A grid needs a property."));
      }
      const body = payload(property.id);
      return grid ? updateGrid(grid.id, body) : createGrid(body);
    },
    {
      onSuccess: (saved) => {
        refetch?.();
        // A ladder nobody uses moves no price, so there is nothing to check.
        if (saved.assignment_count > 0) {
          setChecking(saved);
          return;
        }
        onClose();
      },
      onError,
    }
  );

  const submit = () => {
    setSubmitted(true);
    if (!canSave) return;
    save.mutate();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
      {isShared && (
        <Alert severity="warning">
          <FormattedMessage
            id="PRICING_TIER_SHARED_WARNING"
            values={{ count: grid?.assignment_count ?? 0 }}
          />
        </Alert>
      )}

      <GridEditor
        recoIncreasePcts={productsQuery.data?.reco_increase_pcts ?? []}
        name={name}
        onNameChange={setName}
        roundingDirection={roundingDirection}
        onRoundingDirectionChange={setRoundingDirection}
        uhTypeFollow={uhTypeFollow}
        onUhTypeFollowChange={setUhTypeFollow}
        property={property}
        onPropertyChange={setProperty}
        proposal={proposal}
        onClearProposal={() => {
          setProposal(null);
          setDraft({
            ...draft,
            products: draft.products.map((product) => ({
              ...product,
              prices: {},
            })),
          });
        }}
        draft={draft}
        onDraftChange={setDraft}
        disabled={save.isLoading || loading}
      />

      {showError && errorKey && (
        <Typography variant="caption" color="error.main">
          <FormattedMessage id={errorKey} />
        </Typography>
      )}

      {checking && (
        <ApplyDialog
          grid={checking}
          onClose={() => {
            setChecking(null);
            onClose();
          }}
        />
      )}

      {/* The host passes `actions={null}` to a custom form, so the footer is
          ours — same container and same pair the generic form closes with. */}
      <DialogActions sx={{ width: "100%", px: 0 }}>
        <AppButton intent="tonal" onClick={onClose} sx={SX_FORM_CANCEL}>
          <FormattedMessage id="CANCEL" />
        </AppButton>
        <AppButton
          onClick={submit}
          loading={save.isLoading}
          // Nothing has been priced yet: there is no ladder to submit, and
          // pressing it would only raise an error the analyst cannot act on.
          disabled={!dirty}
          sx={SX_FORM_SUBMIT}
        >
          <FormattedMessage id="SUBMIT" />
        </AppButton>
      </DialogActions>
    </Box>
  );
};

export default LadderForm;
